import React, { useMemo, useRef, useState, useEffect } from 'react';
import Header from '../components/Header';
import EarlyAccessBanner from '../components/EarlyAccessBanner';
import StatsBar from '../components/StatsBar';
import FilterBar from '../components/FilterBar';
import SpriteCard from '../components/SpriteCard';
import SpriteDetailDialog from '../components/SpriteDetailDialog';
import TradeDialog from '../components/TradeDialog';
import TradesSheet from '../components/TradesSheet';
import ShareImageButton from '../components/ShareImageButton';
import LotterySection from '../components/LotterySection';
import SpriteForgeDialog from '../components/SpriteForgeDialog';
import CustomSpritesSection from '../components/CustomSpritesSection';
import CommunityGallery from '../components/CommunityGallery';
import FacebookShareSection from '../components/FacebookShareSection';
import MapDialog from '../components/MapDialog';
import BuySection from '../components/BuySection';
import MerchStrip from '../components/MerchStrip';
import UserSearchBar from '../components/UserSearchBar';
import TopTraders from '../components/TopTraders';
import HomepageDrawReplay from '../components/HomepageDrawReplay';
import { useCollection } from '../hooks/useCollection';
import { useTrades } from '../hooks/useTrades';
import { useCustomSprites } from '../hooks/useCustomSprites';
import { useUserId } from '../hooks/useUserId';
import { usePublicSprites } from '../hooks/usePublicSprites';
import { useTheme } from '../contexts/ThemeContext';
import { SPRITES as BASE_SPRITES, RARITY_ORDER, VARIANT_ORDER, SILHOUETTE, CURRENT_SEASON, seasonForFamily, applySeasonConfig } from '../mock';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Trash2, Share2, Wand2, Map as MapIcon } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const DEFAULT_FILTERS = {
  q: '',
  status: 'all',
  variant: 'all',
  rarity: 'all',
  sort: 'type',
  showUnreleased: false,
  season: CURRENT_SEASON,
};

export default function Tracker() {
  const { collection, getEntry, toggleOwned, setLevel, toggleMastered, reset } = useCollection();
  const { trades, addTrade, updateTrade, removeTrade, clearAll: clearTrades } = useTrades();
  const { sprites: customSprites, add: addCustomSprite, remove: removeCustomSprite } = useCustomSprites();
  const { userId, name: displayName, setName: setDisplayName } = useUserId();
  const {
    sprites: publicSprites,
    loading: publicLoading,
    refresh: refreshPublic,
    publish: publishPublic,
    vote: votePublic,
    unpublish: unpublishPublic,
  } = usePublicSprites();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [dynamicSprites, setDynamicSprites] = useState([]);
  const [seasonCfgVersion, setSeasonCfgVersion] = useState(0);

  // Live season list + family→season map (auto-detected from fortnite.gg).
  useEffect(() => {
    let alive = true;
    api.getSeasons().then((cfg) => {
      if (!alive || !cfg) return;
      const prevDefault = CURRENT_SEASON;
      const next = applySeasonConfig(cfg);
      setFilters((f) => (f.season === prevDefault && next !== prevDefault ? { ...f, season: next } : f));
      setSeasonCfgVersion((v) => v + 1);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Pull sprites the admin has synced from fortnite.gg. They merge into the
  // static SPRITES list below so the rest of the grid, filters, poster etc.
  // treat them exactly like a mock.js entry.
  // Family aliases — fortnite.gg uses "Grim" while mock.js uses "Grim Reaper"
  // and its internal codename "Bushranger" for the Bush family. Normalise the
  // dynamic feed so both feeds group together in the grid.
  const FAMILY_ALIAS = { 'Grim': 'Grim Reaper', 'Bushranger': 'Bush' };

  useEffect(() => {
    let alive = true;
    api.listDynamicSprites().then((rows) => {
      if (!alive || !Array.isArray(rows)) return;
      setDynamicSprites(
        rows.map((r) => ({ ...r, family: FAMILY_ALIAS[r.family] || r.family }))
      );
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Merge mock.js (BASE_SPRITES) + dynamic (fortnite.gg synced) by (family, variant).
  // The dynamic feed is the source of truth for release status — if the mock
  // has a silhouette entry for a family+variant that the dynamic feed shows as
  // released, the dynamic one wins (and vice versa). No duplicates.
  // EXCEPTION: `gem` variants stay unreleased on the tracker even when the
  // dynamic feed reports them released — the community treats gem sprites as
  // still-upcoming/leak-only until they officially drop in-game.
  const SPRITES = useMemo(() => {
    const keyOf = (s) => `${s.family}|${s.variant}`;
    const dynByKey = new Map();
    dynamicSprites.forEach((d) => dynByKey.set(keyOf(d), d));

    const usedDynKeys = new Set();
    // Families the tracker must never upgrade to released via the dynamic
    // feed — art may exist on fortnite.gg (data-mined) but the sprite has
    // not officially dropped in-game yet. (Currently none — Storm Scout
    // dropped in-game so its fortnite.gg art is now the real thing.)
    const KEEP_UNRELEASED = new Set();
    const merged = BASE_SPRITES.map((m) => {
      // Gems are always unreleased on the tracker (community rule).
      const d = dynByKey.get(keyOf(m));
      const season = d?.season || m.season;
      if (m.variant === 'gem') {
        usedDynKeys.add(keyOf(m));
        return { ...m, season, unreleased: true, img: SILHOUETTE };
      }
      if (!d) return m;
      usedDynKeys.add(keyOf(m));
      // Leaked-but-not-yet-live families stay as silhouettes.
      if (KEEP_UNRELEASED.has(m.family)) return { ...m, season, unreleased: true, img: SILHOUETTE };
      // Dynamic feed is the source of truth for artwork — if fortnite.gg
      // has a real image for this (family, variant), always use it so we
      // never render stale mock art when the real sprite has been updated.
      // Preserve the stable mock id + metadata (rarity, drop%, tag) so
      // ownership state and family ordering stay consistent.
      if (!d.unreleased && d.img && d.img !== SILHOUETTE) {
        return { ...m, season, unreleased: false, img: d.img };
      }
      // Fallback: dynamic has no better image — keep mock art.
      return { ...m, season };
    });
    // Add any dynamic sprites that don't have a mock counterpart.
    // Dynamic sprites derive their season from the family (fortnite.gg's
    // 12 new C7S4 debut families are the only ones flagged current).
    dynamicSprites.forEach((d) => {
      if (!usedDynKeys.has(keyOf(d))) {
        const season = d.season || seasonForFamily(d.family);
        merged.push(
          d.variant === 'gem'
            ? { ...d, unreleased: true, img: SILHOUETTE, season }
            : { ...d, season }
        );
      }
    });
    return merged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicSprites, seasonCfgVersion]);
  const [tradeSprite, setTradeSprite] = useState(null);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [forgeOpen, setForgeOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [detailSprite, setDetailSprite] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const lotteryRef = useRef(null);
  const scrollToLottery = () => {
    lotteryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePublishCustom = async (payload) => {
    return publishPublic({
      ...payload,
      owner_id: userId,
      owner_name: payload.owner_name || displayName || 'Anonymous',
    });
  };

  const openTrade = (sprite) => {
    setTradeSprite(sprite);
    setTradeOpen(true);
  };

  const submitTrade = (data) => {
    addTrade(data);
    toast({ title: 'Trade sent!', description: `Trade for ${data.spriteName} sent to ${data.recipient}.` });
  };

  const filtered = useMemo(() => {
    // Build a stable family order from the SPRITES array (first appearance wins),
    // so sorting by "type" preserves the canonical Family + Variant order
    // (base → gold → gummy → galaxy) instead of the legacy ID order.
    const familyOrder = {};
    SPRITES.forEach((s) => {
      if (!(s.family in familyOrder)) familyOrder[s.family] = Object.keys(familyOrder).length;
    });
    const variantIdx = (v) => {
      const i = VARIANT_ORDER.indexOf(v);
      return i === -1 ? 999 : i;
    };
    const typeSort = (a, b) =>
      (familyOrder[a.family] - familyOrder[b.family]) ||
      (variantIdx(a.variant) - variantIdx(b.variant)) ||
      (a.id - b.id);

    const q = filters.q.trim().toLowerCase();
    let arr = SPRITES.filter((s) => {
      if (filters.season !== 'all' && (s.season || 'C7S3') !== filters.season) return false;
      if (!filters.showUnreleased && s.unreleased) return false;
      if (filters.variant !== 'all' && s.variant !== filters.variant) return false;
      if (filters.rarity !== 'all' && s.rarity !== filters.rarity) return false;
      const entry = collection[s.id] || { owned: false, mastered: false };
      if (filters.status === 'owned' && !entry.owned) return false;
      if (filters.status === 'missing' && entry.owned) return false;
      if (filters.status === 'mastered' && !entry.mastered) return false;
      if (q) {
        const blob = `${s.name} ${s.family} ${s.variant} ${s.rarity}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });

    const sortFn = {
      type: typeSort,
      'level-desc': (a, b) => (collection[b.id]?.level || 0) - (collection[a.id]?.level || 0) || typeSort(a, b),
      'level-asc': (a, b) => (collection[a.id]?.level || 0) - (collection[b.id]?.level || 0) || typeSort(a, b),
      'rarity-desc': (a, b) => (RARITY_ORDER[b.rarity] - RARITY_ORDER[a.rarity]) || typeSort(a, b),
      'rarity-asc': (a, b) => (RARITY_ORDER[a.rarity] - RARITY_ORDER[b.rarity]) || typeSort(a, b),
    }[filters.sort];

    arr = [...arr].sort(sortFn);
    return arr;
  }, [SPRITES, filters, collection]);

  const onShare = () => {
    const owned = SPRITES.filter((s) => collection[s.id]?.owned);
    const mastered = SPRITES.filter((s) => collection[s.id]?.mastered);
    const text =
      `My Fortnite Sprite Collection: ${owned.length}/${SPRITES.length} owned · ${mastered.length} mastered\n` +
      owned.map((s) => `${collection[s.id]?.mastered ? '[MASTERED]' : '[OWNED]'} ${s.name} (Lv ${collection[s.id]?.level || 0})`).join('\n');
    navigator.clipboard?.writeText(text);
    toast({ title: 'Copied!', description: 'Collection summary copied to clipboard.' });
  };

  const onReset = () => {
    if (window.confirm('Reset all owned/level/mastered data?')) {
      reset();
      toast({ title: 'Collection reset', description: 'All sprites are back to Not Owned.' });
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--t-bg-base)' }}>
      {/* Continuous colorful gradient — fixed, covers full page */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 8% 6%, var(--t-blob-pink) 0%, transparent 35%),' +
            'radial-gradient(circle at 92% 4%, var(--t-blob-cyan) 0%, transparent 35%),' +
            'radial-gradient(circle at 50% 18%, var(--t-blob-yellow) 0%, transparent 40%),' +
            'radial-gradient(circle at 85% 42%, var(--t-blob-purple) 0%, transparent 38%),' +
            'radial-gradient(circle at 12% 55%, var(--t-blob-teal) 0%, transparent 38%),' +
            'radial-gradient(circle at 75% 75%, var(--t-blob-pink) 0%, transparent 40%),' +
            'radial-gradient(circle at 20% 90%, var(--t-blob-yellow) 0%, transparent 36%),' +
            'radial-gradient(circle at 60% 100%, var(--t-blob-cyan) 0%, transparent 35%)',
          opacity: 'var(--t-blob-opacity)',
        }}
      />
      {/* dotted overlay */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle, var(--t-dot-color) 1px, transparent 1.5px)',
        backgroundSize: '22px 22px'
      }} />
      <div className="relative">
        <Header onLotteryClick={scrollToLottery} />
        <EarlyAccessBanner />
        <StatsBar collection={collection} sprites={SPRITES} showUnreleased={filters.showUnreleased} season={filters.season} />
        <FilterBar filters={filters} setFilters={setFilters} onReset={() => setFilters({ ...DEFAULT_FILTERS, season: CURRENT_SEASON })} />

        {/* Action row */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 mt-4 flex items-center justify-between flex-wrap gap-2 sm:gap-3">
          <div
            data-testid="showing-count"
            className="text-sm font-black px-3 py-1.5 rounded-full border-2 shadow-[0_3px_0_var(--t-card-border)]"
            style={{
              background: 'var(--t-card)',
              color: 'var(--t-text)',
              borderColor: 'var(--t-card-border)',
            }}
          >
            Showing <span className="text-[#ff2bd6]">{filtered.length}</span> sprite{filtered.length === 1 ? '' : 's'}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Map temporarily hidden — flip MAP_ENABLED to true to restore */}
            {false && (
              <Button
                data-testid="open-map-btn"
                className="h-10 rounded-xl border-2 border-[#3a1064] bg-gradient-to-r from-[#00b8ff] via-[#9d3dff] to-[#ff2bd6] text-white font-black shadow-[0_3px_0_var(--t-card-border)]"
                onClick={() => setMapOpen(true)}
              >
                <MapIcon className="h-4 w-4 mr-2" /> Open Map
              </Button>
            )}
            <Button
              className="h-10 rounded-xl border-2 border-[#3a1064] bg-gradient-to-r from-[#ffd900] via-[#ff2bd6] to-[#00b8ff] text-[#3a1064] font-black shadow-[0_3px_0_var(--t-card-border)]"
              onClick={() => setForgeOpen(true)}
            >
              <Wand2 className="h-4 w-4 mr-2" /> Sprite Forge
            </Button>
            <TradesSheet
              trades={trades}
              onUpdate={updateTrade}
              onRemove={removeTrade}
              onClearAll={clearTrades}
            />
            <ShareImageButton collection={collection} sprites={SPRITES} filterStatus={filters.status} season={filters.season} />
            <Button
              variant="outline"
              className="h-10 rounded-xl border-2 font-black shadow-[0_3px_0_var(--t-card-border)] hover:opacity-90"
              style={{
                background: 'var(--t-card)',
                color: 'var(--t-text)',
                borderColor: 'var(--t-card-border)',
              }}
              onClick={onShare}
            >
              <Share2 className="h-4 w-4 mr-2" /> Copy text
            </Button>
            <Button
              variant="outline"
              className="h-10 rounded-xl border-2 text-white bg-[#ff2bd6] hover:bg-[#e119bf] font-black shadow-[0_3px_0_var(--t-card-border)]"
              style={{ borderColor: 'var(--t-card-border)' }}
              onClick={onReset}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Reset
            </Button>
          </div>
        </div>

        {/* Grid */}
        <main className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🔍</div>
              <div className="text-xl font-black" style={{ color: 'var(--t-text)' }}>No sprites match your filters</div>
              <div className="mt-1 font-bold" style={{ color: 'var(--t-text-soft)' }}>Try clearing search or resetting filters.</div>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1.5 sm:gap-4">
              {filtered.map((s) => (
                <SpriteCard
                  key={s.id}
                  sprite={s}
                  entry={getEntry(s.id)}
                  onToggle={toggleOwned}
                  onLevel={setLevel}
                  onMaster={toggleMastered}
                  onTrade={openTrade}
                  onExpand={(sprite) => { setDetailSprite(sprite); setDetailOpen(true); }}
                />
              ))}
            </div>
          )}
        </main>

        {/* Lottery / winners / top traders / user search — moved BELOW the
            sprite grid so users see sprites immediately on page load. */}
        <LotterySection ref={lotteryRef} trades={trades} />
        <HomepageDrawReplay />
        <TopTraders />
        <UserSearchBar />

        {/* Sprite Forge + Community Gallery — below the main tracker */}
        <CustomSpritesSection
          sprites={customSprites}
          onRemove={removeCustomSprite}
          onOpenForge={() => setForgeOpen(true)}
          onPublish={handlePublishCustom}
          publicSprites={publicSprites}
          userId={userId}
          displayName={displayName}
          onChangeDisplayName={setDisplayName}
        />
        <CommunityGallery
          sprites={publicSprites}
          loading={publicLoading}
          userId={userId}
          onVote={votePublic}
          onRefresh={refreshPublic}
          onUnpublish={unpublishPublic}
        />

        <FacebookShareSection
          collection={collection}
          totalSprites={SPRITES.length}
          displayName={displayName}
        />

        <MerchStrip />

        <BuySection />

        <footer
          className="max-w-7xl mx-auto px-3 sm:px-6 py-10 text-center text-xs font-bold"
          style={{ color: 'var(--t-text)' }}
        >
          Unofficial, fan-made tracker. Not affiliated with, endorsed by, sponsored by, or approved by Epic Games, Inc.
          Fortnite and all related marks are trademarks of Epic Games, Inc.
        </footer>
      </div>

      <TradeDialog
        open={tradeOpen}
        onOpenChange={setTradeOpen}
        sprite={tradeSprite}
        onSubmit={submitTrade}
      />

      <SpriteForgeDialog
        open={forgeOpen}
        onOpenChange={setForgeOpen}
        onSaved={addCustomSprite}
      />

      <MapDialog
        open={mapOpen}
        onOpenChange={setMapOpen}
        collection={collection}
        getEntry={getEntry}
        onTrade={openTrade}
      />

      <SpriteDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        sprite={detailSprite}
        entry={detailSprite ? getEntry(detailSprite.id) : null}
        onToggle={toggleOwned}
        onLevel={setLevel}
        onMaster={toggleMastered}
        onTrade={openTrade}
      />
    </div>
  );
}
